// src/views/UserViews/insights/components/FirmwareDetailsDialog.tsx
//
// "View details" dialog for the Firmware Version Distribution card: lists devices behind
// the card's actionable/problem counts (needs update, stuck in bootloader, no reported
// firmware). The healthy "up to date" majority only appears as a header count - this dialog
// exists to let an admin act on problem devices, not re-list everyone.
//
// SCOPING (current): by product request, only "Needs update" is visible - the bootloader/
// unknown sections are hidden behind `SHOW_BOOTLOADER_SECTION`/`SHOW_UNKNOWN_SECTION` (both
// `false`). Visibility-only: classification/counts are still fully computed, so flipping
// either flag back to `true` is a one-line change.
//
// Layout: a summary bar + legend, then one collapsible section per problem tier, each
// showing its first `COLLAPSED_ROW_LIMIT` rows with a "Show N more" reveal. No action
// buttons - this is a read-only reporting query; no DFU/firmware-push capability exists in
// this DAO layer.
//
// Fetches lazily (only while `open`, see the `useEffect` below) and refetches on every
// reopen - low-volume admin tooling, so simplicity beats caching here.
//
// Each row's MAC opens the same device-card overlay a Device Management row click opens -
// see `handleDeviceClick` below for the full/device-only decision.
//
// This dialog stays open behind the device card rather than closing itself first - both need
// to coexist at the same z-index without one breaking the other's accessibility.
// `UserDashboard`'s `Paper` is wrapped in a real MUI `Modal`, so `ModalManager` (the same
// singleton this `Dialog` uses) treats it as the topmost, `aria-hidden`-exempt entry instead
// of an inert sibling under `#root` - without that, MUI's `ariaHiddenSiblings` would hide it
// and break its `FocusTrap` regardless of z-index. Verified with a real-browser test
// asserting `aria-hidden` on both surfaces plus focus landing inside `UserDashboard` on open -
// see `FirmwareDetailsDialog.test.tsx`'s "dialog stays open" suite.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { getFirmwareDeviceDetails } from '@/repository/InsightsDAO';
import { getAllOrgCompassDevices } from '@/repository/OrgCompassDevicesDAO';
import {
  UNKNOWN_VERSION_LABEL,
  type FirmwareDeviceRow,
  type VersionBucket,
} from '@/models/Insights/InsightsTypes';
import {
  BOOTLOADER_FIRMWARE_VERSION,
  computeFirmwareCurrency,
} from '@/models/Insights/versionCurrency';
import { compareVersions, isVersionOutdated } from '@/utils/versionCompare';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import { buildDeviceOverride } from '@/utils/device_helper/buildDeviceOverride';
import type {
  DashboardDeviceOverride,
  OrgCompassDeviceListItem,
} from '@/models/OrgCompassDevice/OrgCompassDeviceListItem';

const LOADING_SKELETON_HEIGHT = 200;

/** Rows per section shown before the "Show N more" reveal - a small, fixed number so an
 * admin scanning the dialog immediately sees the worst offenders in each tier without a
 * long device list pushing the other two sections off screen. */
const COLLAPSED_ROW_LIMIT = 4;

/** Same bar geometry as `FirmwareVersionCurrency`'s own segmented bar, reused verbatim so
 * this dialog's summary bar reads as the same visual language, not a new pattern. */
const BAR_HEIGHT = 8;
const BAR_BORDER_RADIUS = 999;

// SCOPING flags (see header comment) - both `false`: only "Needs update" renders. Checked
// below to gate each section's `<FirmwareDetailsSection>` and to filter `barSegments`.
// Classification/counts stay computed either way.
const SHOW_BOOTLOADER_SECTION = false;
const SHOW_UNKNOWN_SECTION = false;

/** `CompassInfoSnapshot.timestamp` is a required field (seconds since epoch, normally
 * derived from a device row's `last_seen_at` - see `buildDeviceOverride.ts`), but
 * `FirmwareDeviceRow` carries no last-seen timestamp at all (it's sourced from a narrower,
 * three-dimension Cube query - see `getFirmwareDeviceDetails`). `0` is the same "no real
 * timestamp available" fallback `deviceFixtures.ts`'s own test override builder already
 * uses for this field. */
const NO_TIMESTAMP_AVAILABLE = 0;

/**
 * Judgment call: `CompassInfoSnapshot.is_compass_core` is non-nullable, but
 * `FirmwareDeviceRow.isCompassCore` is `boolean | null` (genuinely undetermined - see
 * `InsightsDAO.coerceIsCompassCore`'s doc). Defaults to `false` (Compass Pro, not Core):
 * `isCoreLabel`'s mapping treats `false` as the common case, so an undetermined device reads
 * as ordinary rather than exceptional. Currently has no visible effect on the device-only
 * card (`DeviceInfoSection`/`processDeviceData` key off `hardware_version`, not this field),
 * but the type demands a deliberate choice, not a silent `?? false`.
 */
const DEFAULT_IS_COMPASS_CORE_WHEN_UNDETERMINED = false;

/**
 * MAC-format mismatch risk, verified against both source systems: Azure/CosmosDB's
 * `mac_address` is always canonical uppercase/colon-separated (`MacAddressNormalizer.
 * toCanonical` enforces this server-side), but Cube's `c_mac` is a raw, unnormalized
 * pass-through of the device's telemetry payload - `project_rows.py`'s own fixtures use
 * lowercase in every case but one. A direct `===` between `FirmwareDeviceRow.macAddress`
 * and `OrgCompassDeviceListItem.mac_address` would therefore silently miss real matches, so
 * both sides are normalized (uppercased, separators stripped) as the map key below - this
 * only affects the lookup key, never a displayed string.
 */
const MAC_SEPARATOR_REGEX = /[:-]/g;

function normalizeMacForMatching(macAddress: string): string {
  return macAddress.toUpperCase().replace(MAC_SEPARATOR_REGEX, '');
}

/**
 * `handleDeviceClick`'s fallback builder - used only when the clicked row's MAC isn't (yet)
 * found in `orgDeviceMacMap` below; when found, `buildDeviceOverride` is used instead with
 * that device's richer data.
 *
 * Builds a device-only `DashboardDeviceOverride` from this dialog's narrower
 * `FirmwareDeviceRow` input. `hardware_version`/`battery_percentage` and last-user identity
 * aren't available from this Cube query, so they pass through as `null` (renders as "--";
 * `DeviceOnlyDashboardHeader`'s generic-title fallback applies).
 */
export function buildFirmwareDeviceOverride(
  row: FirmwareDeviceRow,
): DashboardDeviceOverride {
  return {
    macAddress: row.macAddress,
    compassInfoSnapshot: {
      mac_address: row.macAddress,
      // `UNKNOWN_VERSION_LABEL` ("Unknown") is a display sentinel this dialog/DAO layer
      // invented for "no reported firmware" - it is not a real firmware version string, so
      // it must not leak into the device card as one; `null` renders as "--" there instead.
      firmware_revision:
        row.firmwareVersion === UNKNOWN_VERSION_LABEL
          ? null
          : row.firmwareVersion,
      hardware_version: null,
      battery_percentage: null,
      is_compass_core:
        row.isCompassCore ?? DEFAULT_IS_COMPASS_CORE_WHEN_UNDETERMINED,
      timestamp: NO_TIMESTAMP_AVAILABLE,
    },
    lastUserDisplayName: null,
    lastUserPhoneNumber: null,
  };
}

export type FirmwareDeviceBuckets = {
  needsUpdate: FirmwareDeviceRow[];
  bootloader: FirmwareDeviceRow[];
  unknown: FirmwareDeviceRow[];
};

/**
 * Splits `rows` into the same three problem buckets `computeFirmwareCurrency` counts
 * (bootloader/unknown/needsUpdate), in the same exclusion order, using the same shared
 * constants/comparator - never a parallel reimplementation. "Up to date" rows are simply
 * excluded (not returned in any bucket): this dialog only surfaces the three
 * actionable/problem states, not the healthy majority.
 */
export function classifyFirmwareDevices(
  rows: FirmwareDeviceRow[],
  latestVersion: string | null,
): FirmwareDeviceBuckets {
  const buckets: FirmwareDeviceBuckets = {
    needsUpdate: [],
    bootloader: [],
    unknown: [],
  };

  for (const row of rows) {
    if (row.firmwareVersion === UNKNOWN_VERSION_LABEL) {
      buckets.unknown.push(row);
      continue;
    }
    if (row.firmwareVersion === BOOTLOADER_FIRMWARE_VERSION) {
      buckets.bootloader.push(row);
      continue;
    }
    if (
      latestVersion &&
      isVersionOutdated(row.firmwareVersion, latestVersion)
    ) {
      buckets.needsUpdate.push(row);
    }
  }

  return buckets;
}

/**
 * Display-order copy of `rows`, oldest firmware version first, using the shared
 * `compareVersions` comparator (already `Array.prototype.sort`-suitable per its own doc
 * comment) - never a bespoke comparison. Deliberately separate from `classifyFirmwareDevices`
 * above: that function's bucket *contents* are the tested, must-not-change classification;
 * this only reorders the needs-update bucket for display, so the two stay independently
 * reviewable and neither can accidentally leak into the other.
 */
function sortNeedsUpdateOldestFirst(
  rows: FirmwareDeviceRow[],
): FirmwareDeviceRow[] {
  return [...rows].sort((a, b) =>
    compareVersions(a.firmwareVersion, b.firmwareVersion),
  );
}

/**
 * Display-order copy of `rows`, alphabetical by MAC address. Bootloader/unknown rows have no
 * meaningful version to sort by (the whole point of those two buckets is that firmware
 * currency doesn't apply to them), so this just gives them a stable, deterministic order
 * instead of whatever order the DAO happened to return.
 */
function sortByMacAddress(rows: FirmwareDeviceRow[]): FirmwareDeviceRow[] {
  return [...rows].sort((a, b) => a.macAddress.localeCompare(b.macAddress));
}

/**
 * Display label for a row's device identifier: the raw `macAddress`, or a dedicated
 * "unidentified device" fallback when it's `UNKNOWN_VERSION_LABEL` (Rule 1). Deliberately
 * not that constant itself - it's a firmware *version* fallback (InsightsTypes.ts), a
 * different fact that happens to share the same sentinel value.
 */
function deviceIdentifierLabel(
  macAddress: string,
  t: (key: string) => string,
): string {
  return macAddress === UNKNOWN_VERSION_LABEL
    ? t(StringKeys.INSIGHTS_FIRMWARE_DETAILS_UNKNOWN_DEVICE_LABEL)
    : macAddress;
}

/** Small square legend swatch - duplicated locally like `FirmwareVersionCurrency`/
 * `MobileVersionCurrency` already do, rather than a shared module for one swatch. */
function LegendSwatch({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '2px',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

/** Small colored dot marking each section's header - rounder/smaller than `LegendSwatch`
 * so the section header and legend row read as related but distinct. */
function SectionDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

type FirmwareDetailsSectionProps = {
  testId: string;
  color: string;
  title: string;
  subtitle: string;
  rows: FirmwareDeviceRow[];
  /** Right-aligned status value per row - the one piece of per-tier variation, so this is a
   * render prop rather than a third near-duplicate section component. */
  renderRowValue: (row: FirmwareDeviceRow) => ReactNode;
  /** Activates the device-card overlay for a row's MAC - identical regardless of tier. */
  onDeviceClick: (row: FirmwareDeviceRow) => void;
};

/**
 * Shared shape for all three problem-tier sections: colored dot, bold title (its own "(N)"
 * count via i18n), one-line subtitle, then up to `COLLAPSED_ROW_LIMIT` rows with a
 * "Show N more" reveal. Extracted once the three sections turned out identical except which
 * rows they get and how the right-hand value renders.
 */
function FirmwareDetailsSection({
  testId,
  color,
  title,
  subtitle,
  rows,
  renderRowValue,
  onDeviceClick,
}: FirmwareDetailsSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) return null;

  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROW_LIMIT);
  const hiddenCount = rows.length - visibleRows.length;
  // Whether this section has anything to reveal/re-hide - independent of `expanded`, since
  // the toggle (now "Show less") must stay rendered once expanded even though hiddenCount is 0.
  const canToggle = rows.length > COLLAPSED_ROW_LIMIT;

  return (
    <Box data-testid={testId} sx={{ mb: 3, '&:last-of-type': { mb: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SectionDot color={color} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.25, mb: 1.5 }}
      >
        {subtitle}
      </Typography>

      <Box role="list" sx={{ display: 'flex', flexDirection: 'column' }}>
        {visibleRows.map((row) => (
          <Box
            key={row.deviceKey}
            role="listitem"
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 2,
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            {/* role="button"/tabIndex/Enter+Space mirrors DeviceTable.tsx's own mobile-card
                click target, rather than inventing a new accessibility pattern here. */}
            <Typography
              variant="body2"
              component="span"
              role="button"
              tabIndex={0}
              data-testid={`firmware-device-row-mac-${row.deviceKey}`}
              onClick={() => onDeviceClick(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDeviceClick(row);
                }
              }}
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'transparent',
                '&:hover, &:focus-visible': {
                  textDecorationColor: 'currentColor',
                },
              }}
            >
              {deviceIdentifierLabel(row.macAddress, t)}
            </Typography>
            {renderRowValue(row)}
          </Box>
        ))}
      </Box>

      {canToggle && (
        <Button
          variant="text"
          size="small"
          disableRipple
          onClick={() => setExpanded((prev) => !prev)}
          data-testid={`${testId}-show-more`}
          sx={{
            mt: 0.5,
            px: 0,
            minWidth: 0,
            // `px:0`/`minWidth:0` read as a text link, but MUI's default text-Button hover
            // still paints a tinted box + `translateY` lift, reading as a stray rectangle.
            // Plain underline + color-darken instead, so hover is unmistakably a link.
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'primary.dark',
              textDecoration: 'underline',
              transform: 'none',
            },
          }}
        >
          {expanded
            ? t(StringKeys.INSIGHTS_FIRMWARE_DETAILS_SHOW_LESS)
            : t(StringKeys.INSIGHTS_FIRMWARE_DETAILS_SHOW_MORE, {
                count: hiddenCount,
              })}
        </Button>
      )}
    </Box>
  );
}

type FirmwareDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  orgCode: string;
  /** Same "latest published version" value the card itself was given - `null`/empty means
   * the needs-update bucket stays empty (mirrors `computeFirmwareCurrency`'s behavior when
   * the release catalog is loading/failed), while bootloader/unknown are unaffected. */
  latestVersion: string | null;
};

export default function FirmwareDetailsDialog({
  open,
  onClose,
  orgCode,
  latestVersion,
}: FirmwareDetailsDialogProps) {
  const { t } = useTranslation();
  const dashboardActions = useDashboardActions();
  const [rows, setRows] = useState<FirmwareDeviceRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Azure-sourced enrichment for the device-card upgrade in `handleDeviceClick` below - keyed
  // by `normalizeMacForMatching(mac_address)` to match a Cube row's MAC despite the
  // casing/separator mismatch (see that function's own comment). Resets to an empty `Map` on
  // every open, only grows on success. If a MAC is clicked before this resolves or after it
  // fails, `.get()` simply misses and `handleDeviceClick` falls back to the device-only path
  // for that one click - no blocking, spinner, or disabled state for a rare timing window.
  const [orgDeviceMacMap, setOrgDeviceMacMap] = useState<
    Map<string, OrgCompassDeviceListItem>
  >(new Map());

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows(null);
    setOrgDeviceMacMap(new Map());

    // Two-argument `.then(onFulfilled, onRejected)` - both handlers attach to this one
    // promise in a single call, matching `useFirmwareLookupTable`/`useInsightsData`'s own
    // fetch-on-mount convention (rather than a separate `.catch()` on a derived promise).
    getFirmwareDeviceDetails(orgCode).then(
      (data) => {
        if (cancelled) return;
        setRows(data);
        setLoading(false);
      },
      (fetchError: unknown) => {
        console.error(
          'FirmwareDetailsDialog: getFirmwareDeviceDetails failed',
          fetchError,
        );
        if (cancelled) return;
        setError(
          fetchError instanceof Error
            ? fetchError
            : new Error(String(fetchError)),
        );
        setLoading(false);
      },
    );

    // Fired in parallel with the Cube fetch above (not sequentially after it) - independent
    // of `rows`/`loading`/`error`; only consumer is `handleDeviceClick`'s full/device-only
    // decision, not this dialog's own rendering.
    getAllOrgCompassDevices().then(
      (orgDevices) => {
        if (cancelled) return;
        const map = new Map<string, OrgCompassDeviceListItem>();
        for (const orgDevice of orgDevices) {
          map.set(normalizeMacForMatching(orgDevice.mac_address), orgDevice);
        }
        setOrgDeviceMacMap(map);
      },
      (fetchError: unknown) => {
        // Enrichment is optional: log and leave orgDeviceMacMap empty rather than touching
        // this dialog's shared error/loading state - degrades to "every click opens
        // device-only", never this dialog's own error screen.
        console.error(
          'FirmwareDetailsDialog: getAllOrgCompassDevices failed',
          fetchError,
        );
      },
    );

    return () => {
      cancelled = true;
    };
  }, [open, orgCode]);

  // Opens the device-card overlay on top of this still-open dialog (see the header comment
  // for why both can be open at once). `onClose()` is deliberately NOT called - this dialog
  // stays mounted underneath the device card. `dashboardActions.openDashboard` comes from
  // the Zustand store shared with `DeviceTable.tsx`'s own click handler; `UserDashboard`
  // must be mounted by an ancestor page (see `Insights.tsx`) for this to render anything.
  const handleDeviceClick = (row: FirmwareDeviceRow) => {
    const orgDevice = orgDeviceMacMap.get(
      normalizeMacForMatching(row.macAddress),
    );
    if (orgDevice) {
      // Same call shape as `DeviceTable.openDevice` - opens the identical full/device-only
      // card that row's own Device Management entry would.
      dashboardActions.openDashboard(
        orgDevice.last_auth_id,
        buildDeviceOverride(orgDevice),
      );
      return;
    }

    dashboardActions.openDashboard(null, buildFirmwareDeviceOverride(row));
  };

  const buckets = useMemo(
    () => classifyFirmwareDevices(rows ?? [], latestVersion),
    [rows, latestVersion],
  );

  const bootloaderRows = useMemo(
    () => sortByMacAddress(buckets.bootloader),
    [buckets.bootloader],
  );
  const unknownRows = useMemo(
    () => sortByMacAddress(buckets.unknown),
    [buckets.unknown],
  );
  const needsUpdateRows = useMemo(
    () => sortNeedsUpdateOldestFirst(buckets.needsUpdate),
    [buckets.needsUpdate],
  );

  // `upToDateCount` isn't derivable from `buckets` above (that function never returns
  // up-to-date rows), so the subtitle needs `computeFirmwareCurrency` itself, fed this
  // dialog's own rows (one `VersionBucket` per row) instead of a second query.
  const rowsAsBuckets: VersionBucket[] = useMemo(
    () =>
      (rows ?? []).map((row) => ({
        version: row.firmwareVersion,
        group: '',
        count: 1,
      })),
    [rows],
  );
  const currency = useMemo(
    () => computeFirmwareCurrency(rowsAsBuckets, latestVersion),
    [rowsAsBuckets, latestVersion],
  );
  // Subtitle states current-vs-outdated, not a whole-fleet size (product decision - see
  // `INSIGHTS_FIRMWARE_DETAILS_DIALOG_SUBTITLE`'s copy).
  const upToDateCount = currency.upToDate;
  // Reuses `needsUpdateRows.length` (the same value driving the "Needs update (N)" section
  // header) rather than a second `currency.needsUpdate` source - both apply the same
  // exclusion rule today, but reusing the section's own number guarantees the subtitle can't
  // silently drift from what it's summarizing.
  const outdatedCount = needsUpdateRows.length;

  const bootloaderSectionTitle = t(
    StringKeys.INSIGHTS_FIRMWARE_DETAILS_BOOTLOADER_SECTION_TITLE,
    {
      deviceCount: bootloaderRows.length,
    },
  );
  const unknownSectionTitle = t(
    StringKeys.INSIGHTS_FIRMWARE_DETAILS_UNKNOWN_SECTION_TITLE,
    {
      deviceCount: unknownRows.length,
    },
  );
  const needsUpdateSectionTitle = t(
    StringKeys.INSIGHTS_FIRMWARE_DETAILS_NEEDS_UPDATE_SECTION_TITLE,
    {
      deviceCount: needsUpdateRows.length,
    },
  );

  // Mirrors the subtitle's own "on current release" wording, not the card's percent-based
  // "Up to date — N (P%)" copy (see `INSIGHTS_FIRMWARE_DETAILS_CURRENT_RELEASE_LEGEND`'s doc).
  const currentReleaseLegendLabel = t(
    StringKeys.INSIGHTS_FIRMWARE_DETAILS_CURRENT_RELEASE_LEGEND,
    {
      deviceCount: upToDateCount,
    },
  );

  // Bar/legend tiers, in order: up-to-date (first, so the bar mirrors the subtitle's
  // current-vs-outdated framing), then needs-update, then this dialog's two dialog-only
  // tiers (unknown, bootloader) when visible. Colors match `FirmwareVersionCurrency`'s own
  // tier colors (success/warning), except bootloader reuses `error.main` here (a real
  // failure signal per `versionCurrency.ts`'s header) - that card's bar never colors this
  // tier at all. One flat color per tier, never graduated by how far behind (Decision 1).
  //
  // `unknown`/`bootloader` entries are only included when their `SHOW_*` flag is `true` (see
  // header comment) - `upToDate` is never gated, since the subtitle above always shows that
  // count unconditionally. Filtering here keeps `totalBarDevices` honest about only counting
  // visible tiers.
  const barSegments: Array<{
    key: string;
    color: string;
    count: number;
    label: string;
  }> = [
    {
      key: 'upToDate',
      color: 'success.main',
      count: upToDateCount,
      label: currentReleaseLegendLabel,
    },
    {
      key: 'needsUpdate',
      color: 'warning.main',
      count: needsUpdateRows.length,
      label: needsUpdateSectionTitle,
    },
    ...(SHOW_UNKNOWN_SECTION
      ? [
          {
            key: 'unknown',
            color: 'grey.400',
            count: unknownRows.length,
            label: unknownSectionTitle,
          },
        ]
      : []),
    ...(SHOW_BOOTLOADER_SECTION
      ? [
          {
            key: 'bootloader',
            color: 'error.main',
            count: bootloaderRows.length,
            label: bootloaderSectionTitle,
          },
        ]
      : []),
  ];
  // Sum of every visible segment's count - gates whether the bar/legend render at all.
  // (`flexGrow: segment.count` below needs no explicit total; flexGrow ratios self-normalize.)
  const totalBarDevices = barSegments.reduce(
    (sum, segment) => sum + segment.count,
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {t(StringKeys.INSIGHTS_FIRMWARE_DETAILS_DIALOG_TITLE)}
          <IconButton
            aria-label={t(StringKeys.CLOSE)}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {!loading && !error && rows && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="firmware-details-subtitle"
          >
            {t(StringKeys.INSIGHTS_FIRMWARE_DETAILS_DIALOG_SUBTITLE, {
              upToDateCount,
              outdatedCount,
            })}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Skeleton
            variant="rectangular"
            height={LOADING_SKELETON_HEIGHT}
            data-testid="firmware-details-loading"
          />
        )}

        {!loading && error && (
          <Typography
            variant="body2"
            color="error"
            data-testid="firmware-details-error"
          >
            {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
          </Typography>
        )}

        {!loading && !error && (
          <>
            {totalBarDevices > 0 && (
              <Box data-testid="firmware-details-summary-bar" sx={{ mb: 3 }}>
                <Box
                  data-testid="firmware-details-bar-track"
                  sx={{
                    display: 'flex',
                    height: BAR_HEIGHT,
                    borderRadius: BAR_BORDER_RADIUS,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                  }}
                >
                  {barSegments.map(
                    (segment) =>
                      segment.count > 0 && (
                        <Box
                          key={segment.key}
                          data-testid={`firmware-details-bar-segment-${segment.key}`}
                          sx={{
                            flexGrow: segment.count,
                            bgcolor: segment.color,
                          }}
                        />
                      ),
                  )}
                </Box>
                <Box
                  data-testid="firmware-details-legend"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    mt: 0.75,
                  }}
                >
                  {barSegments
                    .filter((segment) => segment.count > 0)
                    .map((segment) => (
                      <Box
                        key={segment.key}
                        data-testid={`firmware-details-legend-row-${segment.key}`}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <LegendSwatch color={segment.color} />
                        <Typography variant="caption" color="text.secondary">
                          {segment.label}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            <FirmwareDetailsSection
              testId="firmware-details-needs-update-section"
              color="warning.main"
              title={needsUpdateSectionTitle}
              subtitle={t(
                StringKeys.INSIGHTS_FIRMWARE_DETAILS_NEEDS_UPDATE_SECTION_SUBTITLE,
              )}
              rows={needsUpdateRows}
              // Decision 1: one flat, neutral color for every row, never graduated by how
              // far behind the version is - no ordered release history exists to rank by
              // (`versionCurrency.ts`'s "Rule 8"). Same neutral `text.secondary` styling as
              // the other two sections' row values - a version number isn't a severity signal.
              renderRowValue={(row) => (
                <Typography variant="body2" color="text.secondary">
                  {row.firmwareVersion}
                </Typography>
              )}
              onDeviceClick={handleDeviceClick}
            />

            {/* SCOPING (current): gated behind `SHOW_UNKNOWN_SECTION` (see header comment) -
                `false` today, so this section (header, rows, "Show N more") does not render.
                `unknownRows`/`unknownSectionTitle` above are still computed either way. */}
            {SHOW_UNKNOWN_SECTION && (
              <FirmwareDetailsSection
                testId="firmware-details-unknown-section"
                color="grey.400"
                title={unknownSectionTitle}
                subtitle={t(
                  StringKeys.INSIGHTS_FIRMWARE_DETAILS_UNKNOWN_SECTION_SUBTITLE,
                )}
                rows={unknownRows}
                renderRowValue={() => (
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      StringKeys.INSIGHTS_FIRMWARE_DETAILS_NO_VERSION_REPORTED,
                    )}
                  </Typography>
                )}
                onDeviceClick={handleDeviceClick}
              />
            )}

            {/* SCOPING (current): gated behind `SHOW_BOOTLOADER_SECTION` (see header comment)
                - `false` today, so this section (header, rows, "Show N more") does not render.
                `bootloaderRows`/`bootloaderSectionTitle` above are still computed either way. */}
            {SHOW_BOOTLOADER_SECTION && (
              <FirmwareDetailsSection
                testId="firmware-details-bootloader-section"
                color="error.main"
                title={bootloaderSectionTitle}
                subtitle={t(
                  StringKeys.INSIGHTS_FIRMWARE_DETAILS_BOOTLOADER_SECTION_SUBTITLE,
                )}
                rows={bootloaderRows}
                renderRowValue={() => (
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      StringKeys.INSIGHTS_FIRMWARE_DETAILS_NO_VERSION_REPORTED,
                    )}
                  </Typography>
                )}
                onDeviceClick={handleDeviceClick}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
